import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LoginLinkDto } from '@motoschool/shared';
import { api, ApiRequestError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { Button, Card, CopyableLink, ErrorNotice, PageLoader, SectionTitle } from './ui';
import { formatDateTime } from '../lib/format';

interface LoginLinkCardProps {
  userId: string;
  /** Кто заходит по ссылке — от этого зависит только текст подсказки. */
  subject: 'Ученик' | 'Инструктор' | 'Сотрудник';
}

/**
 * Ссылка, по которой человек заходит в свой кабинет без пароля.
 *
 * Для сотрудников без email и телефона это единственный вход: логина у них
 * нет, а пароль вводить некуда. Перевыпуск гасит прежнюю ссылку — иначе
 * «перевыпустить» ничего не значило бы, а разосланные раньше адреса
 * продолжали бы пускать внутрь.
 */
export function LoginLinkCard({ userId, subject }: LoginLinkCardProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const { timeZone } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['login-link', userId],
    queryFn: () => api.get<{ link: LoginLinkDto | null }>(`/users/${userId}/login-link`),
  });

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['login-link', userId] });

  const issue = useMutation({
    mutationFn: () => api.post<{ link: LoginLinkDto }>(`/users/${userId}/login-link`, {}),
    onSuccess: invalidate,
    onError: (caught: unknown) =>
      setError(caught instanceof ApiRequestError ? caught.message : 'Не удалось создать ссылку'),
  });

  const revoke = useMutation({
    mutationFn: () => api.delete(`/users/${userId}/login-link`),
    onSuccess: invalidate,
    onError: (caught: unknown) =>
      setError(caught instanceof ApiRequestError ? caught.message : 'Не удалось отозвать ссылку'),
  });

  const link = data?.link ?? null;

  return (
    <Card>
      <SectionTitle>Вход по ссылке</SectionTitle>

      {error && <ErrorNotice message={error} />}

      {isLoading ? (
        <PageLoader />
      ) : link ? (
        <div className="space-y-3">
          <CopyableLink url={link.url} />

          <p className="text-xs text-muted">
            {link.expiresAt
              ? `Действует до ${formatDateTime(link.expiresAt, timeZone)}`
              : 'Действует бессрочно'}
            {link.useCount > 0 && ` · переходов: ${link.useCount}`}
          </p>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              isLoading={issue.isPending}
              onClick={() => {
                setError(null);
                issue.mutate();
              }}
            >
              Перевыпустить
            </Button>
            <Button
              variant="danger"
              isLoading={revoke.isPending}
              onClick={() => {
                setError(null);
                revoke.mutate();
              }}
            >
              Отозвать
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted">
            {subject} открывает ссылку и сразу попадает в кабинет — пароль вводить не нужно.
          </p>
          <Button
            fullWidth
            isLoading={issue.isPending}
            onClick={() => {
              setError(null);
              issue.mutate();
            }}
          >
            Создать ссылку
          </Button>
        </div>
      )}
    </Card>
  );
}
